import Box from '@mui/material/Box';
import type { DistanceTreeItem} from 'common/components/Elements/DistanceTree/DistanceTree';
import DistanceTree, { getDistanceTreeItemTree } from 'common/components/Elements/DistanceTree/DistanceTree';
import { arrayIntersection, arrayLast } from 'common/utilities/core/array';
import { precisionRound } from 'common/utilities/core/number';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import type { AppDispatch } from 'core/redux/store/configureStore.types';
import { ThemeColors } from 'core/utilities/theme';
import React, {
  useCallback, useEffect, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { arrangeBySimilarity, similarityTree } from 'sequenceAnalyser/api/api';
import type { Sequence, SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import { addIdsToSelection, removeIdsFromSelection } from 'sequenceAnalyser/redux/actions/sequenceAnalyserActions';
import { getSequenceLabel } from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';
import { calcLongestDistance, distanceTreeItemsFromDistanceTreeNode } from 'sequenceAnalyser/utilities/similarityTree/similarityTree';

import type { ShowDistanceToggle } from './useSimilarityTreeMenu';

export const leafDistance = 16;

interface ComponentProps {
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  width: number;
  SortBySimilarityLoading?: JSX.Element;
  canvasId: string;
  showDistanceToggle: ShowDistanceToggle;
}

function SimilarityTree({
  sequences, alignedSequences, width, SortBySimilarityLoading, canvasId, showDistanceToggle,
}: ComponentProps) {
  const dispatch = useDispatch<AppDispatch>();
  const selection = useSelector((state: ApplicationState) => state.sequenceAnalyser.selection);
  const labelComposition = useSelector((state: ApplicationState) => state.sequenceAnalyser.settings.labelComposition);
  const alignmentOptions = useSelector((state: ApplicationState) => arrayLast(state.sequenceAnalyser.ranAlignments));

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<DistanceTreeItem[]>([]);

  const height = items.filter((i) => i.position !== undefined).length * leafDistance;

  const getItemLabel = useCallback((sequenceId: number) => {
    const sequence = sequences.find((s) => s.id === sequenceId);
    return sequence ? getSequenceLabel(sequence, labelComposition) : '';
  }, [labelComposition, sequences]);

  const fetchTree = useCallback(async () => {
    setLoading(true);

    // First, sort by similarity
    const sortedAlignedSequences = await arrangeBySimilarity(alignedSequences, alignmentOptions);

    // Then get the tree
    const tree = await similarityTree(sortedAlignedSequences);

    const longestDistance = calcLongestDistance(tree, 0);
    let newItems = distanceTreeItemsFromDistanceTreeNode(
      tree, undefined, longestDistance, 0, tree.distance, leafDistance,
    );

    // Populate items with sequences names
    newItems = newItems.map((item) => ({
      ...item,
      label: getItemLabel(item.id),
      labelExtra: item.position === undefined ? undefined : precisionRound(item.nodeDistance, 1).toString(),
    }));

    setItems(newItems);
    setLoading(false);
  }, [alignedSequences, alignmentOptions, getItemLabel]);

  const handleClick = (item: DistanceTreeItem) => {
    const selectedItems = getDistanceTreeItemTree(items, item);
    const selectedLeafItems = selectedItems.filter((i) => !selectedItems.some((si) => si.parentId === i.id));
    const ids = selectedLeafItems.map((i) => Number(i.id));
    const isLeaf = ids.length === 1;

    if (isLeaf) {
      if (selection.ids.includes(ids[0])) {
        dispatch(removeIdsFromSelection(ids));
      } else {
        dispatch(addIdsToSelection(ids));
      }
    } else if (arrayIntersection(selection.ids, ids).length === ids.length) {
      // All ids within a node are already selected: unselect all
      dispatch(removeIdsFromSelection(ids));
    } else {
      // Not all ids within a node are selected: select all
      dispatch(addIdsToSelection(ids));
    }
  };

  useEffect(() => {
    fetchTree();
  }, [alignedSequences, fetchTree]);

  return (
    <Box sx={{ mt: 1 }}>
      {SortBySimilarityLoading}

      {!loading && items.length > 0 && (
        <DistanceTree
          id={canvasId}
          width={width}
          height={height}
          orientation="vertical"
          direction="default"
          items={items}
          selectedItems={selection.ids}
          onClick={handleClick}
          leafDistance={leafDistance}
          color={ThemeColors.textColor}
          selectedColor={ThemeColors.sequenceAnalyserSelection}
          showLabel
          showLabelExtra={showDistanceToggle === 'on'}
        />
      )}
    </Box>
  );
}
export default SimilarityTree;
